import { useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { useAsync } from '../../api/useAsync';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { can } from '../../auth/capabilities';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { ReconcileReport, Tenant, TrailEntry } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Link } from '../../router/Router';
import { Ago } from '../../ui/Ago';
import { Button, Card, Chip, EmptyState, Loading, PageHead, Stat } from '../../ui/primitives';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { DataTable } from '../../ui/DataTable';
import type { Column } from '../../ui/DataTable';
import { FilterBar, SegmentedFilter } from '../../ui/form';
import { RedirectUriNotice } from '../../ui/RedirectUriNotice';
import { ResourceTable } from '../../ui/ResourceTable';
import {
  IdentityStateBadge,
  OutcomeBadge,
  StatusBadge,
  TenantStateBadge,
} from '../../ui/StatusBadge';
import { IconChevronLeft } from '../../ui/icons';
import { useToast } from '../../ui/Toast';
import { IdentityProviderSummary } from './IdentityProviderSummary';
import { failureKey, isFailureState, stateKey } from './identityChoice';
import { trailTone } from './trail';
import {
  attemptsOf,
  budgetAlertTone,
  budgetBlock,
  budgetMeaningKey,
  budgetNeedsSaying,
  certificateBlock,
  dated,
  filled,
  nameStanding,
  outcomeLabelKey,
  outcomeMeaningKey,
  outcomeTone,
  readingFinding,
  readingLabelKey,
  readingOrder,
  readingTone,
  relayPublished,
  roleLabelKey,
  settingsBlock,
  trustLabelKey,
  trustMeaningKey,
  trustOf,
  trustTone,
  unreadNames,
  validityOf,
} from './reconcileReport';

/**
 * One tenancy, as provider staff sees it.
 *
 * Eight cards in a column became a head, four figures and a section at a time.
 * The change is not tidiness: the eight had nothing in common but the slug, and
 * the one control anybody came for — applying what is declared — was inside a
 * band halfway down. It is in the head now, where a control belongs, and the
 * state that band announced is one of the four figures.
 *
 * The identity provider is shown and not edited, and that is not a layout
 * decision: there is no route that changes it by slug, for anyone. Which
 * provider a tenancy's people sign in through is the tenancy's decision, made
 * from the tenancy's own account — an operator who could change it could point
 * a customer's cluster at a provider whose tokens they mint, and then sign in.
 *
 * The trail is new here and was not new to the server: `…/audit` has been
 * answering all along with the hundred last things this control plane did to
 * the tenancy, and nothing showed them. It is the most ordinary thing a console
 * has and the one this card was missing.
 */
export function TenantScreen({ slug }: { slug: string }) {
  const { t } = useI18n();
  const api = useAccountApi();

  const state = useAsync((signal) => api.provider.getTenant(slug, signal), `tenant:${slug}`);
  const [section, setSection] = useState<Section>('overview');
  const [failure, setFailure] = useState<ApiError | null>(null);

  const back = (
    <Link to={{ name: 'account.tenants' }} className="btn btn-ghost btn-sm">
      <IconChevronLeft size={16} />
      {t('tenant.back')}
    </Link>
  );

  if (state.status === 'loading') {
    return (
      <div className="content-inner">
        <Loading />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="content-inner">
        <div style={{ marginBottom: 18 }}>{back}</div>
        {state.error.code === 'tenant_not_found' ? (
          <Card>
            <EmptyState title={t('tenant.notFound.title')} body={t('tenant.notFound.body')} />
          </Card>
        ) : (
          <ErrorAlert
            error={state.error}
            action={
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={state.reload}>
                  {t('common.retry')}
                </Button>
              </div>
            }
          />
        )}
      </div>
    );
  }

  const tenant = state.value;
  const report = tenant.last_reconcile;

  return (
    <div className="content-inner">
      <div style={{ marginBottom: 18 }}>{back}</div>

      <PageHead
        title={tenant.display_name}
        sub={tenant.tenancy_domain}
        actions={
          <>
            <Apply
              tenant={tenant}
              onApplied={state.reload}
              onFailed={(error) => {
                setFailure(error);
              }}
            />
            <Link to={{ name: 'account.usage', slug: tenant.slug }} className="btn btn-ghost">
              {t('usage.title')}
            </Link>
          </>
        }
      />

      <div className="grid g-4" style={{ marginBottom: 'var(--gap)' }}>
        <Stat label={t('tenant.field.state')} value={<TenantStateBadge state={tenant.state} />} />
        <Generation tenant={tenant} />
        <Certificate report={report} />
        <Identity tenant={tenant} />
      </div>

      {/* The result of pressing the button in the head, where it was pressed. */}
      {failure !== null && (
        <div style={{ marginBottom: 'var(--gap)' }}>
          <ErrorAlert error={failure} />
        </div>
      )}

      <FilterBar
        filters={
          <SegmentedFilter<Section>
            legend={t('tenant.sections.legend')}
            value={section}
            onChange={setSection}
            options={[
              { value: 'overview', label: t('tenant.section.overview') },
              { value: 'certificate', label: t('certificate.section') },
              { value: 'budget', label: t('budget.section') },
              { value: 'identity', label: t('tenant.section.idp') },
              { value: 'catalog', label: t('catalog.title') },
              { value: 'reconcile', label: t('reconcile.section') },
              { value: 'trail', label: t('trail.section') },
            ]}
          />
        }
      />

      {section === 'overview' && <Overview tenant={tenant} />}
      {section === 'certificate' && <CertificateSection report={report} />}
      {section === 'budget' && <AnonymousSurface report={report} />}
      {section === 'identity' && <IdentitySection tenant={tenant} />}
      {section === 'catalog' && <DeclaredCatalogue slug={tenant.slug} />}
      {section === 'reconcile' && <LastReconcile report={report} />}
      {section === 'trail' && <Trail slug={tenant.slug} />}
    </div>
  );
}

type Section =
  | 'overview'
  | 'certificate'
  | 'budget'
  | 'identity'
  | 'catalog'
  | 'reconcile'
  | 'trail';

/**
 * Applying what has been declared, from the head.
 *
 * It answers when it is done — a reconcile is measured in seconds, and somebody
 * who pressed it wants to know what happened rather than be told it has been
 * queued.
 */
function Apply({
  tenant,
  onApplied,
  onFailed,
}: {
  tenant: Tenant;
  onApplied: () => void;
  onFailed: (error: ApiError) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();
  const { session } = useAccountSession();
  const toast = useToast();

  const [running, setRunning] = useState(false);

  if (!can(session, 'reconcile_tenant')) return null;

  async function apply() {
    setRunning(true);
    try {
      const run = await api.provider.reconcile(tenant.slug);
      toast(t('reconcile.done', { outcome: run.report.outcome }));
      onApplied();
    } catch (error: unknown) {
      onFailed(toApiError(error));
    } finally {
      setRunning(false);
    }
  }

  return (
    <Button
      variant={tenant.up_to_date ? 'ghost' : 'primary'}
      disabled={running}
      onClick={() => void apply()}
    >
      {running ? `${t('reconcile.running')}…` : t('reconcile.submit')}
    </Button>
  );
}

/** What has been declared and what the cluster was last brought to. */
function Generation({ tenant }: { tenant: Tenant }) {
  const { t, formatNumber } = useI18n();

  return (
    <Stat
      label={t('tenant.tile.generation')}
      value={
        <span className="mono">
          {formatNumber(tenant.generation)}
          {' / '}
          {tenant.applied_generation === null
            ? t('tenants.declared.never')
            : formatNumber(tenant.applied_generation)}
        </span>
      }
      {...(tenant.up_to_date ? {} : { tone: 'warn' as const, sub: t('tenant.declared.behind') })}
    />
  );
}

/** How the last pass over the certificate came out, and how long it lasts. */
function Certificate({ report }: { report: ReconcileReport | undefined }) {
  const { t, formatDate } = useI18n();

  const block = certificateBlock(report);
  if (!block.present) {
    return <Stat label={t('certificate.section')} absent={{ label: t('common.unread') }} />;
  }

  const certificate = block.value;
  const validity = validityOf(certificate);

  return (
    <Stat
      label={t('certificate.section')}
      value={
        <StatusBadge tone={outcomeTone(certificate)} label={t(outcomeLabelKey(certificate))} />
      }
      {...(dated(validity) && validity.notAfter !== undefined
        ? { sub: t('tenancy.certificate.until', { when: formatDate(validity.notAfter) }) }
        : {})}
    />
  );
}

/** Which provider this tenancy's people sign in through, and where that stands. */
function Identity({ tenant }: { tenant: Tenant }) {
  const { t } = useI18n();

  const identity = tenant.identity;
  if (identity === undefined) {
    return <Stat label={t('identityProvider.title')} absent={{ label: t('common.unread') }} />;
  }

  const kind = identity.applied?.kind;
  return (
    <Stat
      label={t('identityProvider.title')}
      value={<IdentityStateBadge state={identity.state} />}
      {...(kind === undefined
        ? {}
        : {
            sub: t(
              kind === 'provider' ? 'identityProvider.kind.provider' : 'identityProvider.kind.own',
            ),
          })}
    />
  );
}

/** Everything that identifies the tenancy, in one list. */
function Overview({ tenant }: { tenant: Tenant }) {
  const { t, formatDate } = useI18n();

  return (
    <Card>
      <div className="card-pad">
        <dl className="dl">
          <dt>{t('tenant.field.slug')}</dt>
          <dd className="mono">{tenant.slug}</dd>
          <dt>{t('tenant.field.consoleDomain')}</dt>
          <dd className="mono">{tenant.console_domain}</dd>
          <dt>{t('tenant.field.gatewayDomain')}</dt>
          <dd className="mono">{tenant.gateway_domain}</dd>
          <dt>{t('tenant.field.adminGroup')}</dt>
          <dd className="mono">{tenant.admin_group}</dd>
          <dt>{t('tenant.field.version')}</dt>
          <dd className="mono">{tenant.version}</dd>
          <dt>{t('tenant.field.created')}</dt>
          <dd>{formatDate(tenant.created_at)}</dd>
          <dt>{t('tenant.field.updated')}</dt>
          <dd>{formatDate(tenant.updated_at)}</dd>
          <dt>{t('tenant.section.fingerprint')}</dt>
          <dd className="mono" style={{ wordBreak: 'break-all' }}>
            {tenant.transport_ca_fingerprint ?? t('tenant.fingerprint.none')}
          </dd>
        </dl>

      </div>
    </Card>
  );
}

/**
 * The certificate the tenancy's gateway and console are published under, as the
 * last application read it off the disk.
 *
 * Two things are said out loud and the rest waits under one disclosure. The two
 * are states that are wrong now: a renewal that keeps failing, which is visible
 * for weeks before anything expires, and a tenancy with no certificate — which
 * publishes no registration relay, which is why a connector at the customer's own
 * site has nowhere to spend its join token. That last one is otherwise heard as
 * "the customer's connectors will not come up", from the customer.
 *
 * The two sets of names are a mark and two columns of chips. Whether they agree
 * is the answer; why both sets are shown at all is a paragraph, and it is one
 * press away.
 */
function CertificateSection({ report }: { report: ReconcileReport | undefined }) {
  const { t, formatDateTime } = useI18n();

  const block = certificateBlock(report);
  if (!block.present) {
    return (
      <Card>
        <div className="card-pad">
          <p className="muted">{t(block.reasonKey)}</p>
        </div>
      </Card>
    );
  }

  const certificate = block.value;
  const names = nameStanding(certificate);
  const trust = trustOf(certificate);
  const validity = validityOf(certificate);
  const attempts = attemptsOf(certificate);
  const authority = filled(certificate.authority);
  const detail = filled(certificate.detail);

  return (
    <div className="stack">
      {/* A failure that repeats on a schedule is a wait. One with no next
          attempt named is not, and reads differently. */}
      {attempts.kind === 'waiting' && (
        <Alert tone="warn" title={t('certificate.attempts.waiting.title')}>
          <p>
            {t('certificate.attempts.waiting.body', {
              attempts: attempts.attempts,
              when: formatDateTime(attempts.retryAfter),
            })}
          </p>
        </Alert>
      )}
      {attempts.kind === 'stalled' && (
        <Alert tone="deny" title={t('certificate.attempts.stalled.title')}>
          <p>{t('certificate.attempts.stalled.body', { attempts: attempts.attempts })}</p>
        </Alert>
      )}
      {!relayPublished(certificate) && (
        <Alert tone="warn" title={t('certificate.relay.title')}>
          <p>{t('certificate.relay.body')}</p>
          <p style={{ marginTop: 8 }}>{t('certificate.relay.nearby')}</p>
        </Alert>
      )}

      <Card>
        <div className="card-pad">
          <dl className="dl">
            <dt>{t('reconcile.col.outcome')}</dt>
            <dd>
              <StatusBadge
                tone={outcomeTone(certificate)}
                label={t(outcomeLabelKey(certificate))}
                hint={t(outcomeMeaningKey(certificate))}
              />
            </dd>
            <dt>{t('certificate.authority')}</dt>
            <dd className={authority === undefined ? 'muted' : 'mono'}>
              {authority ?? t('certificate.authority.none')}
            </dd>
            <dt>{t('certificate.trust.section')}</dt>
            <dd>
              <StatusBadge
                tone={trustTone(trust)}
                label={t(trustLabelKey(trust))}
                hint={t(trustMeaningKey(trust))}
              />
            </dd>
            <dt>{t('certificate.valid.notAfter')}</dt>
            <dd className={dated(validity) ? '' : 'muted'}>
              {!dated(validity)
                ? t('certificate.valid.none')
                : validity.notAfter === undefined
                  ? t('certificate.valid.noDate')
                  : formatDateTime(validity.notAfter)}
            </dd>
            <dt>{t('certificate.valid.renewAt')}</dt>
            <dd className={dated(validity) ? '' : 'muted'}>
              {!dated(validity)
                ? t('certificate.valid.none')
                : validity.renewAt === undefined
                  ? t('certificate.valid.noDate')
                  : formatDateTime(validity.renewAt)}
            </dd>
          </dl>

          {/* The server's own sentence, which is the only place the reason for a
              refusal or the zone of an untended tenancy is named. */}
          {detail !== undefined && (
            <p className="mono" style={{ marginTop: 12 }}>
              {detail}
            </p>
          )}

          <p className="section-title">{t('certificate.names.section')}</p>
          <div className="row" style={{ marginBottom: 10 }}>
            {names.kind === 'agree' ? (
              <StatusBadge
                tone="ok"
                label={t('certificate.names.agree.badge')}
                hint={t('certificate.names.agree')}
              />
            ) : (
              <StatusBadge tone="warn" label={t('certificate.names.diverged.title')} />
            )}
          </div>
          <div className="grid g-2">
            <div>
              <p className="faint">{t('certificate.names.wanted')}</p>
              <div className="row" style={{ marginTop: 6 }}>
                {certificate.wanted.map((name) => (
                  <Chip key={name}>{name}</Chip>
                ))}
              </div>
            </div>
            <div>
              <p className="faint">{t('certificate.names.installed')}</p>
              {certificate.installed.length === 0 ? (
                <p className="muted" style={{ marginTop: 6 }}>
                  {t('certificate.names.installed.none')}
                </p>
              ) : (
                <div className="row" style={{ marginTop: 6 }}>
                  {certificate.installed.map((name) => (
                    <Chip key={name}>{name}</Chip>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Published and not covered is the half that fails the step, so it is
              named rather than left to be worked out from two lists of chips. */}
          {names.kind === 'diverged' && names.missing.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Alert tone="warn" title={t('certificate.names.missing')}>
                <div className="row">
                  {names.missing.map((name) => (
                    <Chip key={name}>{name}</Chip>
                  ))}
                </div>
              </Alert>
            </div>
          )}

        </div>
      </Card>
    </div>
  );
}

/**
 * The budget the tenancy's gateway holds an anonymous caller to, and — the part
 * worth the section — whether the gateway's **process** has read it.
 *
 * gdsgate reads `[proxy]` once, when it starts, so writing the numbers and the
 * numbers taking effect are two events with a window between them, and nothing
 * closes that window by restarting anything: recreating a gateway drops every
 * live session and every connector tunnel. `not_read_yet` is proof and stands as
 * the finding it is; `not_known` is the absence of an answer and says so.
 */
function AnonymousSurface({ report }: { report: ReconcileReport | undefined }) {
  const { t, formatNumber } = useI18n();

  const block = budgetBlock(report);
  if (!block.present) {
    return (
      <Card>
        <div className="card-pad">
          <p className="muted">{t(block.reasonKey)}</p>
        </div>
      </Card>
    );
  }

  const budget = block.value;
  const detail = filled(budget.detail);

  return (
    <div className="stack">
      <div className="grid g-3">
        <Stat label={t('budget.rate')} value={formatNumber(budget.rate_per_ip)} />
        <Stat label={t('budget.burst')} value={formatNumber(budget.burst)} />
        <Stat
          label={t('settings.section')}
          value={
            <StatusBadge
              tone={readingTone(budget.state)}
              label={t(readingLabelKey(budget.state))}
              hint={t(budgetMeaningKey(budget.state))}
            />
          }
        />
      </div>

      {/* Said out loud for every state but the ordinary one: each of the others
          means the tenancy is not, or may not be, under the number above. */}
      {budgetNeedsSaying(budget.state) && (
        <Alert tone={budgetAlertTone(budget.state)} title={t(readingLabelKey(budget.state))}>
          <p>{t(budgetMeaningKey(budget.state))}</p>
          {readingFinding(budget.state) && (
            <p style={{ marginTop: 8 }}>{t('budget.state.notReadYet.noRestart')}</p>
          )}
          {budget.state === 'not_known' && (
            <p style={{ marginTop: 8 }}>{t('budget.state.notKnown.notAFinding')}</p>
          )}
        </Alert>
      )}

      <Card>
        <div className="card-pad">
          {detail !== undefined && <p className="mono">{detail}</p>}
        </div>
      </Card>
    </div>
  );
}

/** Which provider the tenancy's people come through, shown and not set. */
function IdentitySection({ tenant }: { tenant: Tenant }) {
  const { t } = useI18n();

  const identity = tenant.identity;

  return (
    <Card>
      <div className="card-pad">
        {identity === undefined ? (
          <p className="muted">{t('tenant.idp.absent')}</p>
        ) : (
          <>
            <IdentityProviderSummary
              choice={identity.applied}
              providerIssuer={identity.provider_issuer}
            />

            {identity.in_flight && (
              <div style={{ marginTop: 14 }}>
                <Alert tone="info" title={t(stateKey(identity.state))}>
                  {t('tenant.idp.applying')}
                </Alert>
              </div>
            )}

            {isFailureState(identity.state) && (
              <div style={{ marginTop: 14 }}>
                <Alert tone="warn" title={t(stateKey(identity.state))}>
                  <p>{t(failureKey(identity.failure))}</p>
                  <p style={{ marginTop: 8 }}>{t('tenant.idp.failed')}</p>
                </Alert>
              </div>
            )}

            {/* The customer registers this at their provider, and support is
                asked for it by people who cannot reach their own console. */}
            <div style={{ marginTop: 14 }}>
              <RedirectUriNotice value={identity.redirect_uri} />
            </div>
          </>
        )}

      </div>
    </Card>
  );
}

/**
 * What this tenancy has declared for its cluster to serve — read, and only
 * read.
 *
 * There is no button here and no route behind one: writing a catalogue is done
 * in the cluster console, where the person doing it is somebody that tenancy's
 * own identity provider vouched for and their name reaches that tenancy's audit
 * trail.
 */
function DeclaredCatalogue({ slug }: { slug: string }) {
  const { t, plural } = useI18n();
  const api = useAccountApi();

  const state = useAsync((signal) => api.provider.listResources(slug, signal), `resources:${slug}`);

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('catalog.title')}</span>
        {state.status === 'ready' && (
          <span className="ca faint">{plural('catalog.count', state.value.length)}</span>
        )}
      </div>
      <div className="card-pad">
        {state.status === 'loading' && <Loading />}

        {state.status === 'error' && (
          <ErrorAlert
            error={state.error}
            action={
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={state.reload}>
                  {t('common.retry')}
                </Button>
              </div>
            }
          />
        )}

        {state.status === 'ready' &&
          (state.value.length === 0 ? (
            <p className="muted">{t('catalog.none.body')}</p>
          ) : (
            <ResourceTable resources={state.value} />
          ))}

      </div>
    </Card>
  );
}

/** The last application, step by step. Nothing here carries a secret. */
function LastReconcile({ report }: { report: ReconcileReport | undefined }) {
  const { t, formatDate } = useI18n();

  if (report === undefined) {
    return (
      <Card>
        <div className="card-pad">
          <p className="muted">{t('reconcile.none')}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('reconcile.section')}</span>
        <span className="ca">
          <OutcomeBadge outcome={report.outcome} />
        </span>
      </div>
      <div className="card-pad">
        <dl className="dl">
          <dt>{t('reconcile.field.actor')}</dt>
          <dd className="mono">{report.actor}</dd>
          <dt>{t('reconcile.field.finished')}</dt>
          <dd>{formatDate(report.finished_at)}</dd>
        </dl>

        <div className="tbl-wrap" style={{ marginTop: 14 }}>
          <table className="tbl">
            <caption className="visually-hidden">{t('reconcile.section')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('reconcile.col.step')}</th>
                <th scope="col">{t('reconcile.col.outcome')}</th>
                <th scope="col">{t('reconcile.col.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {report.steps.map((step) => (
                <tr key={`${String(step.contract_step)}:${step.step}`}>
                  <th scope="row" className="mono">
                    {/* Zero is the server's way of saying "not a step of
                        gdsgate's provisioning contract", so it is said in words
                        rather than printed as a number nobody can look up. */}
                    {step.contract_step === 0 ? (
                      <span className="faint" title={t('reconcile.step.beforeContract.hint')}>
                        {t('reconcile.step.beforeContract')}
                      </span>
                    ) : (
                      <>
                        {step.contract_step}
                        {'.'}
                      </>
                    )}
                    {' '}
                    {step.step}
                  </th>
                  <td>
                    <OutcomeBadge outcome={step.outcome} />
                  </td>
                  <td>{step.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <RolesReading report={report} />
      </div>
    </Card>
  );
}

/**
 * What each role has read of what the configuration step rendered for it.
 *
 * It belongs beside that step and nowhere else: the step says what this pass
 * *moved*, which is all that is known while the roles are still down, and this
 * says whether the process holding each role has read it — which is known only
 * after the roles are up.
 *
 * The values are listed **by name**. A name is what an operator acts on, the
 * card is readable by everybody who may read the tenancy, and the fields these
 * names point at are limits today and could be a secret in a later version.
 */
function RolesReading({ report }: { report: ReconcileReport }) {
  const { t } = useI18n();

  const block = settingsBlock(report);
  if (!block.present) {
    return (
      <>
        <p className="section-title">{t('settings.section')}</p>
        <p className="muted">{t(block.reasonKey)}</p>
      </>
    );
  }

  const roles = readingOrder(block.value);

  return (
    <>
      <p className="section-title">{t('settings.section')}</p>

      <div className="stack">
        {roles.map((role) => {
          const labelKey = roleLabelKey(role.role);
          const names = unreadNames(role.unread);
          return (
            <div key={role.role}>
              <div className="row">
                {/* A role this console has words for reads as words; one it does
                    not prints the server's own name, in the type that says so. */}
                {labelKey === undefined ? (
                  <span className="mono">{role.role}</span>
                ) : (
                  <span>{t(labelKey)}</span>
                )}
                <StatusBadge
                  tone={readingTone(role.state)}
                  label={t(readingLabelKey(role.state))}
                />
              </div>
              <p className="muted" style={{ marginTop: 6 }}>
                {role.detail}
              </p>
              {names.length > 0 ? (
                <dl className="dl" style={{ marginTop: 8 }}>
                  <dt>{t('settings.unread')}</dt>
                  <dd className="mono">
                    {names.map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </dd>
                </dl>
              ) : (
                <p className="hint" style={{ marginTop: 8 }}>
                  {t('settings.unread.none')}
                </p>
              )}
            </div>
          );
        })}
      </div>

    </>
  );
}

/**
 * What this control plane did to the tenancy, and who asked for it.
 *
 * The words are the server's — `tenant.reconcile`, `catalog.put`,
 * `rollout.move`, and the outcomes they are filed under — and they are shown as
 * they arrive: the set grows on the server's side, and a console that
 * translated them would go quiet on the entry that mattered on the day a new
 * one appeared. Only the colour is this console's.
 *
 * The hundred is the server's limit and there is no cursor, which the count
 * says. Nothing here pretends to page through a record whose beginning was cut
 * off before it arrived.
 */
function Trail({ slug }: { slug: string }) {
  const { t, plural } = useI18n();
  const api = useAccountApi();

  const state = useAsync((signal) => api.provider.readTrail(slug, signal), `trail:${slug}`);
  const entries = state.status === 'ready' ? state.value : [];

  const columns: Column<TrailEntry>[] = [
    {
      key: 'at',
      header: t('trail.col.at'),
      sort: (a, b) => a.seq - b.seq,
      cell: (entry) => <Ago at={entry.at} />,
    },
    {
      key: 'actor',
      header: t('trail.col.actor'),
      mono: true,
      sort: (a, b) => a.actor.localeCompare(b.actor),
      cell: (entry) => entry.actor,
    },
    {
      key: 'action',
      header: t('trail.col.action'),
      mono: true,
      sort: (a, b) => a.action.localeCompare(b.action),
      cell: (entry) => entry.action,
    },
    {
      key: 'outcome',
      header: t('trail.col.outcome'),
      sort: (a, b) => a.outcome.localeCompare(b.outcome),
      cell: (entry) => <StatusBadge tone={trailTone(entry.outcome)} label={entry.outcome} />,
    },
    {
      key: 'detail',
      header: t('trail.col.detail'),
      mono: true,
      hideSm: true,
      cell: (entry) => entry.detail,
    },
  ];

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('trail.section')}</span>
        {state.status === 'ready' && (
          <span className="ca faint">
            {entries.length >= TRAIL_LIMIT ? t('trail.capped') : plural('trail.count', entries.length)}
          </span>
        )}
      </div>
      <div className="card-pad">
        {state.status === 'loading' && <Loading />}

        {state.status === 'error' && (
          <ErrorAlert
            error={state.error}
            action={
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={state.reload}>
                  {t('common.retry')}
                </Button>
              </div>
            }
          />
        )}

        {state.status === 'ready' && (
          <DataTable<TrailEntry>
            caption={t('trail.section')}
            columns={columns}
            rows={entries}
            rowKey={(entry) => String(entry.seq)}
            initialSort={{ key: 'at', direction: 'desc' }}
            empty={{ title: t('trail.none.title'), body: t('trail.none.body') }}
            noMatch={{ title: t('trail.none.title') }}
          />
        )}
      </div>
    </Card>
  );
}

/** The server's own ceiling on one answer. Said here because the count says it. */
const TRAIL_LIMIT = 100;
